import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient }            from "../../api/apiServices";

const LINE_TONE = {
  pending:    "muted",
  processing: "warning",
  paid:       "success",
  failed:     "danger",
};
const LINE_LABEL = { pending: "Pending", processing: "Processing", paid: "Paid", failed: "Failed" };

const RUN_TONE = {
  pending:    "muted",
  processing: "warning",
  completed:  "success",
  partial:    "warning",
  failed:     "danger",
};

export default function PayrollRunPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [tab, setTab]         = useState("run");
  const [preview, setPreview] = useState(null);
  const [runs, setRuns]       = useState([]);
  const [stage, setStage]     = useState("idle");
  const [pin, setPin]         = useState("");
  const [busy, setBusy]       = useState(false);
  const [activeRun, setActiveRun] = useState(null);

  const loadPreview = () => {
    apiClient.post(`/mtrade/companies/${companyId}/payroll/preview`, {})
      .then(r => { if (r.data.status) setPreview(r.data.data); })
      .catch(() => {});
  };
  const loadHistory = () => {
    apiClient.get(`/mtrade/companies/${companyId}/payroll`)
      .then(r => { if (r.data.status) setRuns(r.data.data); })
      .catch(() => {});
  };
  useEffect(() => { loadPreview(); loadHistory(); }, [companyId]);

  useEffect(() => {
    if (!activeRun || ["completed", "failed"].includes(activeRun.status)) return;
    const id = setInterval(async () => {
      try {
        const r = await apiClient.get(`/mtrade/companies/${companyId}/payroll/${activeRun._id}`);
        if (r.data.status) {
          setActiveRun(r.data.data);
          if (["completed", "partial", "failed"].includes(r.data.data.status)) { loadHistory(); clearInterval(id); }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(id);
  }, [activeRun, companyId]);

  const runPayroll = async () => {
    if (pin.length !== 4) return toast.error("Enter your 4-digit PIN");
    setBusy(true);
    try {
      const r = await apiClient.post(`/mtrade/companies/${companyId}/payroll/run`, { transactionPin: pin });
      if (r.data.status) {
        toast.success(r.data.message);
        setActiveRun(r.data.data);
        setStage("idle"); setPin("");
        loadPreview();
      } else toast.error(r.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Payroll failed");
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-bg pb-28">
      <div className="bg-surface px-5 pt-12 pb-0 border-b border-border">
        <div className="flex items-center gap-3 max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <h1 className="text-primary font-bold text-lg font-display">Payroll</h1>
        </div>
        <div className="flex gap-4 max-w-3xl mx-auto mt-3">
          {[["run", "Run Payroll"], ["history", "History"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className="pb-3 text-sm font-semibold border-b-2 transition-colors"
              style={{
                borderColor: tab === k ? "var(--color-brand)" : "transparent",
                color:       tab === k ? "var(--color-brand-text)" : "var(--color-text-muted)",
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-5 max-w-3xl mx-auto space-y-4">
        {tab === "run" && (
          <>
            {activeRun && (
              <div className="bg-surface border border-border rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-secondary font-bold text-sm">Run in progress</p>
                  <span className="text-xs font-bold capitalize" style={{ color: RUN_TONE[activeRun.status] === "muted" ? "var(--color-text-muted)" : `var(--color-${RUN_TONE[activeRun.status]}-text)` }}>{activeRun.status}</span>
                </div>
                <div className="space-y-2">
                  {activeRun.lines.map(l => {
                    const tone = LINE_TONE[l.status] || LINE_TONE.pending;
                    return (
                      <div key={l.staffId} className="flex items-center justify-between text-sm">
                        <span className="text-secondary truncate">{l.fullName}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-muted">₦{Number(l.amount).toLocaleString()}</span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{
                              background: tone === "muted" ? "var(--color-surface-hover)" : `var(--color-${tone}-light)`,
                              color:      tone === "muted" ? "var(--color-text-muted)" : `var(--color-${tone}-text)`,
                            }}>{LINE_LABEL[l.status] || l.status}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-faint text-xs mt-3">{activeRun.paidCount} paid · {activeRun.failedCount} failed of {activeRun.lines.length}</p>
              </div>
            )}

            {preview && (
              <div className="bg-surface border border-border rounded-2xl p-4">
                <p className="text-secondary font-bold text-sm mb-3">Upcoming payroll</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-muted">Staff</span><span className="font-semibold text-primary">{preview.count}</span></div>
                  <div className="flex justify-between"><span className="text-muted">Total</span><span className="font-semibold text-primary">₦{Number(preview.total).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-muted">Wallet balance</span><span className="font-semibold text-primary">₦{Number(preview.walletBalance).toLocaleString()}</span></div>
                </div>

                {!preview.sufficient && (
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-danger-light border border-danger-border rounded-xl mt-3">
                    <i className="ri-error-warning-line text-sm mt-0.5" style={{ color: "var(--color-danger)" }} />
                    <p className="text-xs" style={{ color: "var(--color-danger-text)" }}>Insufficient company wallet. Top up ₦{Number(preview.total - preview.walletBalance).toLocaleString()} more before running payroll.</p>
                  </div>
                )}

                {stage === "idle" && (
                  <button onClick={() => setStage("pin")} disabled={!preview.count || !preview.sufficient}
                    className="w-full mt-4 bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-3.5 rounded-2xl font-bold text-sm transition-all">
                    Run Payroll · ₦{Number(preview.total).toLocaleString()}
                  </button>
                )}

                {stage === "pin" && (
                  <div className="mt-4 space-y-3">
                    <p className="text-secondary text-sm font-semibold text-center">Enter your transaction PIN to authorise</p>
                    <input type="password" inputMode="numeric" maxLength={4} value={pin}
                      onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="••••" autoFocus
                      className="w-full bg-surface-muted border border-border-strong rounded-xl px-3 py-3 text-center tracking-[0.5em] text-lg outline-none focus:border-brand text-primary" />
                    <div className="flex gap-2">
                      <button onClick={() => { setStage("idle"); setPin(""); }} className="flex-1 py-3 rounded-xl bg-surface-hover text-secondary text-sm font-semibold">Cancel</button>
                      <button onClick={runPayroll} disabled={busy || pin.length !== 4} className="flex-1 py-3 rounded-xl bg-brand text-on-brand text-sm font-bold">
                        {busy ? "Processing…" : "Confirm & Pay"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {preview && preview.count === 0 && (
              <p className="text-center text-muted text-sm py-6">No active staff to pay. Add staff first.</p>
            )}
          </>
        )}

        {tab === "history" && (
          runs.length === 0 ? (
            <p className="text-center text-muted text-sm py-10">No payroll runs yet.</p>
          ) : (
            runs.map(run => (
              <button key={run._id} onClick={() => { setTab("run"); setActiveRun(run); }}
                className="w-full bg-surface border border-border rounded-2xl p-4 text-left hover:border-brand-border transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-primary font-semibold text-sm">₦{Number(run.totalAmount).toLocaleString()}</p>
                    <p className="text-muted text-xs">{run.lines.length} staff · {new Date(run.payDate).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}</p>
                  </div>
                  <span className="text-xs font-bold capitalize" style={{ color: RUN_TONE[run.status] === "muted" ? "var(--color-text-muted)" : `var(--color-${RUN_TONE[run.status]}-text)` }}>{run.status}</span>
                </div>
                <p className="text-faint text-xs mt-2">{run.paidCount} paid · {run.failedCount} failed</p>
              </button>
            ))
          )
        )}
      </div>
    </div>
  );
}
